import React, { FC, useContext, useEffect, useState } from "react";
import { DatePicker, Select, Table } from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { useMutation, useQuery } from "react-query";
import dayjs, { Dayjs } from "dayjs";

import { User } from "src/api/types";
import GlobalContext from "src/context/global-context";
import { APISomethingWrongMsg, NetworkDisconnectMsg } from "src/lib/constants";
import { getErrorMessage } from "src/lib/utils";
import client from "../../api";

const { RangePicker } = DatePicker;

type Props = {
  user: User;
};

interface UserActivityLog {
  id: number;
  action: string;
  admin_user_id: number;
  admin_name: string;
  admin_email: string;
  target_user_id: number;
  old_status: string | null;
  new_status: string | null;
  reason: string | null;
  result: string;
  failure_reason: string | null;
  ip_address: string | null;
  request_trace_id: string | null;
  module: string | null;
  action_timestamp: string;
  created_at: string;
}

interface UserActivityLogsResponse {
  status: string;
  code: number;
  message: string;
  data: Record<string, unknown>;
  items: UserActivityLog[];
  totalcount: number;
  page: number;
  limit: number | null;
  totalPages: number | null;
}

interface UserActivityActionsResponse {
  status: string;
  code: number;
  message: string;
  data: string[];
  items: [];
  totalcount: number;
  status_code: number;
}

interface GetUserActivityLogsParams {
  target_user_id: number;
  page: number;
  limit: number;
  action?: string;
  start_date?: string;
  end_date?: string;
}

type DateRangeValue = [Dayjs | null, Dayjs | null] | null;

const UserActivityLogsTab: FC<Props> = ({ user }) => {
  const [dataSource, setDataSource] = useState<UserActivityLog[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const [selectedAction, setSelectedAction] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<DateRangeValue>(null);

  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);

  /* ---------------- ACTIONS LIST ---------------- */

  const [actions, setActions] = useState<string[]>([]);

  const { mutate: getActivityLogActions, isLoading: isActionsLoading } =
    useMutation<UserActivityActionsResponse, unknown>(
      client.User.getActivityLogActions,
      {
        onSuccess: (data) => {
          setActions(data.data || []);
        },

        onError: (error: unknown) => {
          const apiError = error as {
            code?: string;
            response?: {
              data: unknown;
            };
          };

          if (apiError.code === "ERR_NETWORK") {
            setAlertText(NetworkDisconnectMsg);
          } else if (apiError.response) {
            setAlertText(getErrorMessage(apiError.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }

          setIsAlertOpened(true);
        },
      },
    );
  /* ---------------- ACTIVITY LOGS ---------------- */

  const { mutate: getUserActivityLogs, isLoading } = useMutation<
    UserActivityLogsResponse,
    unknown,
    GetUserActivityLogsParams
  >(client.User.getActivityLogs, {
    onSuccess: (data) => {
      setDataSource(data.items || []);
      setTotalCount(data.totalcount || 0);

      if (data.message === "You do not have access for this route!") {
        setAlertText(data.message);
        setIsAlertOpened(true);
      }
    },

    onError: (error: unknown) => {
      const apiError = error as {
        code?: string;
        response?: {
          data: unknown;
        };
      };

      if (apiError.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else if (apiError.response) {
        setAlertText(getErrorMessage(apiError.response.data));
      } else {
        setAlertText(APISomethingWrongMsg);
      }

      setIsAlertOpened(true);
    },
  });

  /* ---------------- FETCH LOGS ---------------- */

  const fetchActivityLogs = (
    page = pagination.current,
    limit = pagination.pageSize,
    action = selectedAction,
    dates = dateRange,
  ) => {
    if (!user?.id) return;

    const params: GetUserActivityLogsParams = {
      target_user_id: user.id,
      page,
      limit,
    };

    if (action) {
      params.action = action;
    }

    if (dates?.[0]) {
      params.start_date = dates[0].format("YYYY-MM-DD");
    }

    if (dates?.[1]) {
      params.end_date = dates[1].format("YYYY-MM-DD");
    }

    getUserActivityLogs(params);
  };

  /* ---------------- INITIAL LOAD ---------------- */

  useEffect(() => {
    if (user?.id) {
      setPagination({
        current: 1,
        pageSize: 10,
      });

      fetchActivityLogs(1, 10);
    }
  }, [user?.id]);

  useEffect(() => {
    getActivityLogActions();
  }, []);

  /* ---------------- ACTION FILTER ---------------- */

  const handleActionChange = (value: string | undefined) => {
    setSelectedAction(value);

    setPagination((previous) => ({
      ...previous,
      current: 1,
    }));

    fetchActivityLogs(1, pagination.pageSize, value, dateRange);
  };

  /* ---------------- DATE FILTER ---------------- */

  const handleDateRangeChange = (dates: DateRangeValue) => {
    setDateRange(dates);

    setPagination((previous) => ({
      ...previous,
      current: 1,
    }));

    fetchActivityLogs(1, pagination.pageSize, selectedAction, dates);
  };

  /* ---------------- PAGINATION ---------------- */

  const handleTableChange = (tablePagination: TablePaginationConfig) => {
    const current = tablePagination.current || 1;
    const pageSize = tablePagination.pageSize || 10;

    setPagination({
      current,
      pageSize,
    });

    fetchActivityLogs(current, pageSize, selectedAction, dateRange);
  };

  const columns: ColumnsType<UserActivityLog> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
    },
    {
      title: "Admin Name",
      dataIndex: "admin_name",
      key: "admin_name",
    },
    {
      title: "Admin Email",
      dataIndex: "admin_email",
      key: "admin_email",
    },
    {
      title: "Old Status",
      dataIndex: "old_status",
      key: "old_status",
      render: (value: string | null) => value || "-",
    },
    {
      title: "New Status",
      dataIndex: "new_status",
      key: "new_status",
      render: (value: string | null) => value || "-",
    },
    {
      title: "Result",
      dataIndex: "result",
      key: "result",
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      render: (value: string | null) => value || "-",
    },
    {
      title: "IP Address",
      dataIndex: "ip_address",
      key: "ip_address",
      render: (value: string | null) => value || "-",
    },
    {
      title: "Module",
      dataIndex: "module",
      key: "module",
      render: (value: string | null) => value || "-",
    },
    {
      title: "Action Timestamp",
      dataIndex: "action_timestamp",
      key: "action_timestamp",
      render: (value: string) => new Date(value).toLocaleString(),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (value: string) => new Date(value).toLocaleString(),
    },
  ];

  return (
    <div className="custom-scroll flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-5">
        <Select
        //   allowClear
        //   showSearch
          placeholder="Filter by action"
          className="min-w-[220px]"
          loading={isActionsLoading}
          value={selectedAction}
          onChange={handleActionChange}
          options={actions.map((action) => ({
            label: action.replaceAll("_", " "),
            value: action,
          }))}
        />

        <RangePicker
          value={dateRange}
          onChange={handleDateRangeChange}
          format="YYYY-MM-DD"
        />
      </div>

      <Table<UserActivityLog>
        columns={columns}
        dataSource={dataSource}
        rowKey="id"
        loading={isLoading}
        scroll={{ x: "max-content" }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: totalCount,
          showTotal: (total) => `Total ${total} activity logs`,
        }}
        onChange={handleTableChange}
      />
    </div>
  );
};

export default UserActivityLogsTab;
